'use client';
import React, { useState } from 'react';
import { Clock, Save, Loader2 } from 'lucide-react';
import Toggle from '@/components/ui/Toggle';

interface ChildTimeConfig {
  childId: string;
  name: string;
  avatar: string;
  dailyLimitEnabled: boolean;
  dailyLimitHours: number;
  weekendLimitEnabled: boolean;
  weekendLimitHours: number;
  bedtimeEnabled: boolean;
  bedtimeHour: string;
}

const initialConfigs: ChildTimeConfig[] = [
  { childId: 'child-001', name: 'Amara Osei', avatar: '🌟', dailyLimitEnabled: true, dailyLimitHours: 2, weekendLimitEnabled: false, weekendLimitHours: 4, bedtimeEnabled: true, bedtimeHour: '21:00' },
  { childId: 'child-002', name: 'Kofi Osei', avatar: '🚀', dailyLimitEnabled: true, dailyLimitHours: 1, weekendLimitEnabled: true, weekendLimitHours: 2, bedtimeEnabled: true, bedtimeHour: '20:00' },
];

export default function ParentTimeControls() {
  const [configs, setConfigs] = useState<ChildTimeConfig[]>(initialConfigs);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);
  const [activeChild, setActiveChild] = useState('child-001');

  const current = configs.find((c) => c.childId === activeChild)!;

  const update = (updates: Partial<ChildTimeConfig>) => {
    setConfigs((p) => p.map((c) => c.childId === activeChild ? { ...c, ...updates } : c));
  };

  const handleSave = async () => {
    setSaving(true);
    // BACKEND INTEGRATION: PUT /api/parental-controls/time with configs
    await new Promise((r) => setTimeout(r, 800));
    setSaving(false);
    setSavedMsg(true);
    setTimeout(() => setSavedMsg(false), 2000);
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Clock size={16} className="text-primary" />
        <h2 className="text-base font-bold text-foreground">Time Controls</h2>
      </div>

      {/* Child tabs */}
      <div className="flex gap-1 bg-muted rounded-lg p-1 mb-4">
        {configs.map((c) => (
          <button
            key={`time-tab-${c.childId}`}
            onClick={() => setActiveChild(c.childId)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-semibold transition-all ${
              activeChild === c.childId ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <span>{c.avatar}</span>
            <span>{c.name.split(' ')[0]}</span>
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {/* Daily limit */}
        <div className="p-3 rounded-xl border border-border space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-foreground">Daily Time Limit</p>
              <p className="text-xs text-muted-foreground">Weekday learning cap</p>
            </div>
            <Toggle
              checked={current.dailyLimitEnabled}
              onChange={(v) => update({ dailyLimitEnabled: v })}
              size="sm"
            />
          </div>
          {current.dailyLimitEnabled && (
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={0.5}
                max={6}
                step={0.5}
                value={current.dailyLimitHours}
                onChange={(e) => update({ dailyLimitHours: parseFloat(e.target.value) })}
                className="flex-1 accent-primary"
              />
              <span className="text-sm font-bold text-foreground font-tabular w-12 text-right">{current.dailyLimitHours}h</span>
            </div>
          )}
        </div>

        {/* Weekend limit */}
        <div className="p-3 rounded-xl border border-border space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-foreground">Weekend Limit</p>
              <p className="text-xs text-muted-foreground">Saturday & Sunday cap</p>
            </div>
            <Toggle
              checked={current.weekendLimitEnabled}
              onChange={(v) => update({ weekendLimitEnabled: v })}
              size="sm"
            />
          </div>
          {current.weekendLimitEnabled && (
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={0.5}
                max={8}
                step={0.5}
                value={current.weekendLimitHours}
                onChange={(e) => update({ weekendLimitHours: parseFloat(e.target.value) })}
                className="flex-1 accent-primary"
              />
              <span className="text-sm font-bold text-foreground font-tabular w-12 text-right">{current.weekendLimitHours}h</span>
            </div>
          )}
        </div>

        {/* Bedtime */}
        <div className="p-3 rounded-xl border border-border space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-foreground">Bedtime Lock</p>
              <p className="text-xs text-muted-foreground">Block campus access after this time</p>
            </div>
            <Toggle
              checked={current.bedtimeEnabled}
              onChange={(v) => update({ bedtimeEnabled: v })}
              size="sm"
            />
          </div>
          {current.bedtimeEnabled && (
            <input
              type="time"
              value={current.bedtimeHour}
              onChange={(e) => update({ bedtimeHour: e.target.value })}
              className="w-full px-3 py-2 bg-input border border-border rounded-lg text-sm text-foreground outline-none focus:ring-2 focus:ring-ring transition-all"
            />
          )}
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full flex items-center justify-center gap-2 mt-4 py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-xl btn-scale hover:bg-primary/90 transition-colors disabled:opacity-60"
      >
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        {savedMsg ? 'Settings Saved ✓' : saving ? 'Saving...' : 'Save Time Controls'}
      </button>
    </div>
  );
}